import * as React from "react";
import {cx}       from "emotion";

import "./quill-text-editor.scss";

import {ReactQuill, Quill}             from "@Common/libs/quill/quill";
import {Link, makeChain}               from "@Common/react/cst2";
import {Effect}                        from "@Common/react/effect";
import {fragments}                     from "@Common/react/fragments";
import {IgnoreUpdate}                  from "@Common/react/ignore-update";
import {Static2}                       from "@Common/react/static-2";
import {UseRef}                        from "@Common/react/use-ref";
import {formatText}                    from "@Common/tools/cc-integration/tools/format-text";
import {getInvalidMergeFieldsFromText} from "@Common/tools/cc-integration/tools/variable-data-handler";
import {Types}                         from "@Common/types";
import {isDevXd}                       from "@Common/ui-components/debug/is-dev";

// import Quill dependencies
const Embed     = Quill.import("blots/embed");
const Delta     = Quill.import("delta");
const Clipboard = Quill.import("modules/clipboard");
const Keyboard  = Quill.import("modules/keyboard");


const QuillTextEditor = ({
    value,
    fieldId,
    inputMask                    = "",
    setRef,
    onChange,
    onFocusCallback              = null,
    onBlurCallback               = null,
    selectionPosition,
    xdQaName,
    variableData                 = [],
    highlightInvalidMergeFields  = false,
    allowPasteWithFormatting     = false,
}:{
    value                        : string,
    fieldId                      : string,
    inputMask                   ?: string,
    setRef                       : (el:any) => void,
    onChange                     : (value:string) => Promise<void>
    onFocusCallback             ?: () => void,
    onBlurCallback              ?: () => void,
    selectionPosition            : Types.Common.React.Static2.Static2Next<Types.Common.UiComponents.QuillTextEditor.TextFieldSelectionPosition>,
    xdQaName                     : string,
    variableData                ?: Types.Apis.Models.VariableData[],
    highlightInvalidMergeFields ?: boolean
    allowPasteWithFormatting    ?: boolean,
}) => (

    makeChain<JSX.Element>()

    // create refs for reading the current value of the editor to determine if a reload is necessary
    .then("localTextFieldRef", (_, next:Link<JSX.Element, Types.Common.React.Static2.Static2Next<any>|Types.Common.React.UseRef.UseRefNext>) => Static2({next}))
    .then("initialized", (_, next:Link<JSX.Element, Types.Common.React.Static2.Static2Next<boolean>>) => Static2({next}))

    // suppress reload if the value is unchanged
    .then(({localTextFieldRef, initialized}, next:Link<JSX.Element, void>) => IgnoreUpdate({
        when: () => {
            let ignoreUpdate = false;

            const editor = localTextFieldRef?.get()?.editor;
            if (initialized.get()
                && editor
                && value.trim() === convertHTMLContentToPlaintext(editor, localTextFieldRef?.get()?.value).trim()) {
                ignoreUpdate = true;
            }

            if (!ignoreUpdate && isDevXd()) console.log("QuillTextEditor Reloading editor due to value change", {fieldId, initialized: initialized.get(), value, previousValue: convertHTMLContentToPlaintext(editor, localTextFieldRef?.get()?.value)});

            initialized.set(true);
            return ignoreUpdate;
        },
        next,
    }))

    // register quill modules
    .then((_, next:Link<JSX.Element, void>) => {

        // register module to allow stylized chips in place of content
        if (variableData?.length) {
            TemplateMarker.blotName = "TemplateMarker";
            TemplateMarker.tagName = "span";
            Quill.register({
                "formats/TemplateMarker": TemplateMarker
            });
        }

        // strip styling from clipboard paste
        if (!allowPasteWithFormatting) {
            Quill.register("modules/clipboard", PlainTextPaste, true);
        }

        // provide event handlers not directly supported by quill
        Quill.register("modules/customEventHandlers", CustomEventHandlers, true);

        // see note below in the commented out copyMergeTags module
        // register custom copyMergeTags module
        // Quill.register("modules/clipboard", copyMergeTags, true);

        // provide notice if issues if the error field highlighting feature is enabled
        if (highlightInvalidMergeFields) {
            if (isDevXd()) console.warn("QuillTextEditor error field highlighting is enabled. This feature is experimental and may have unexpected behavior.");
            /* TODO: Fix the following bugs if using the highlight feature:
                     1. If typing a character directly before an error field, the cursor is moved to the left by the
                        number of characters that make up the error field minus 1.
                     2. Type "{{badfield}}." and the period is removed after the error field formatting is applied.
                     3. Place cursor after an error field and press backspace, and the cursor moves to another spot
                        within the field after deleting the character. CC is not updated with the new value either.
                     4. If field already contains two merge fields, then type a new one, cursor is moved after first }.
                     5. When error tags are rendered, undo does not work as expected.
            */
        }

        return next();

    })

    // create the textarea ref hook for finding focus caret position
    .then("textFieldRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext|any>) => UseRef({next}))

    // add listener to copy events to capture the clipboard selection and convert merge markers to merge tags in clipboard
    // we need merge tags EX: {{fName}} instead of stylized chips (HTML elements) so when they are pasted the editor recognizes them & converts them back to stylized chips
    .then(({}, next:ComponentLink<typeof fragments>) => fragments(
        next(),
        Effect({
            action: (event) => {
                const listener = (event) => {
                    const selection = window.getSelection();
                    const focusedElem = document.activeElement;

                    // helper function to process each node recursively & convert merge chips at any level of the nodeList
                    const convertMergeChipsToTags = (node) => {
                        if (node.className === "merge-marker" || node.className === "merge-marker error") {
                            const mergeMarkerVal = { data: node.dataset?.marker };
                            node.innerHTML = "";
                            return mergeMarkerVal;
                        } else if (node.className === "remove") {
                            node.outerHTML = "";
                            return { data: "" };
                        } else if (node.className === "merge-tag-text") {
                            node.outerHTML = "";
                            const mergeMarkerVal = { "replace-parent-data": node.dataset?.reference };
                            return mergeMarkerVal;
                        } else {
                            let childNodesArr = Array.from(node.childNodes);
                            childNodesArr = childNodesArr.map(childNode => convertMergeChipsToTags(childNode));
                            // if the only thing in selection is a single merge chip, we need to replace the whole containing span with the merge tag data
                            if (childNodesArr.some(childNode => childNode?.["replace-parent-data"])) {
                                node = {data: childNodesArr.find(childNode => childNode?.["replace-parent-data"])["replace-parent-data"]};
                            // otherwise we need to recreate the node with the processed child nodes
                            } else {
                                node.innerHTML = childNodesArr.map(childNode => childNode?.outerHTML ? childNode?.outerHTML : childNode?.data).join('');
                            }
                            return node;
                        }
                    };

                    // this is a user selection with no range so a copy event was fired off without any selection
                    if (selection.isCollapsed) {
                        return;
                    }

                    // copy event is applied to window but we want to only change clipboard data if the focused element a Quill editor
                    if (focusedElem?.className === "ql-editor") {
                        const range = selection.getRangeAt(0);
                        const selectionNodes = range.cloneContents().childNodes;
                        let selectNodesArray = Array.from(selectionNodes);

                        // loop through the copied nodes & convert any stylized merge chips to merge tags so we can insert them on a paste
                        selectNodesArray.forEach((node, index) => {
                            selectNodesArray[index] = convertMergeChipsToTags(node);
                        });

                        // create string from nodes array & write it to the clipboard
                        const selectedNodesString = selectNodesArray.map(node => node.outerHTML ? node.outerHTML : node.data).join('');
                            navigator.clipboard.writeText(selectedNodesString).catch(err => {
                            console.error('Failed to copy to clipboard: ', err);
                        });
                    }
                };
                window.addEventListener("copy", listener);
                window.addEventListener("cut", listener);
                if (event.type === "copy") {
                    return () => window.removeEventListener("copy", listener);
                }
                if (event.type === "cut") {
                    return () => window.removeEventListener("cut", listener);
                }
            }
        })
    ))

    // create the quill editor
    .then(({textFieldRef, localTextFieldRef}) => (
        <div className="quill-hc6"
            id={`quill-editor-${fieldId}`}
            xd-qa-type="textarea"
            xd-qa-name={xdQaName}
            data-hj-whitelist=""
            onBlur={event => {
                // This fixes an issue where Quill field has focus, then selecting the same field in the CC editor to
                // continue editing. The cursor is moved back to the Quill field without this.
                // In this scenario, the ReactQuill onBlur is not fired, so we need to force the blur event. If moving
                // from Quill to CC editor, there will not be an event relatedTarget, so call the blur only if there is
                // none. Without restricting to null relatedTarget, the cursor position is moved to the start of the
                // field when switching between Quill editors.
                if (!event.relatedTarget) textFieldRef?.getEditor()?.blur();
            }}
        >
            <ReactQuill
                ref={el => {textFieldRef = el; setRef(el); localTextFieldRef.set(el);}}
                theme="snow"
                modules={{
                    toolbar: false,
                    keyboard: {
                        bindings: keyboardBindings
                    },
                    customEventHandlers: true
                }}
                value={value}
                preserveWhitespace={true}
                tabIndex={0}

                // set focus status, which allows ignore of onChange events caused by unnecessary re-renders
                onFocus={(range, source, editor) => {
                    selectionPosition.set({...selectionPosition.get(), hasFocus: true});
                    onFocusCallback && onFocusCallback();
                }}
                onBlur={(previousRange, source, editor) => {
                    selectionPosition.set({...selectionPosition.get(), hasFocus: false});
                    onBlurCallback && onBlurCallback();
                }}

                // record selection range for use in resetting cursor position after changing text and re-rendering
                // the editor
                onChangeSelection={async (range, source, editor) => {
                    if (range === null || !textFieldRef?.getEditor) return;
                    if (source === "silent" && range.index === 0 && range.length === 0) return; // allow silent if there is a value - typing text registers as a silent operation
                    if (source === "self") return;
                    const quill = textFieldRef.getEditor();

                    // find the value start position of the cursor - actual position treats merge fields as one
                    // character while the value position treats them as the length of the merge field
                    const originalText = convertHTMLContentToPlaintext(quill, editor.getHTML());
                    let text = originalText;
                    const valueLength = text.length;

                    let editorStartPosition = range.index;
                    let valueStartPosition = range.index;
                    let valueSelectedLength = range.length; // TODO: Adjust logic below to expand selection length to include merge fields

                    // adjust the selection position to account for merge fields
                    for (const variableDataRecord of variableData) {

                        // look for each occurrence of the merge field
                        while (text.indexOf(variableDataRecord.mergeField) >= 0) {

                            // adjust the position related to the raw value to include the length of the merge field
                            const mergeIndex = text.indexOf(variableDataRecord.mergeField);
                            if (mergeIndex >= 0 && mergeIndex < range.index) {
                                valueStartPosition += variableDataRecord.mergeField.length - 1;
                            }

                            // remove the first occurrence of the merge field
                            text = text.replace(variableDataRecord.mergeField, " ");

                        }
                    }

                    // adjust the selection position to account for merge fields that are not stylized in the editor
                    let variableDataErrors = [];
                    if (highlightInvalidMergeFields) {
                        variableDataErrors = getInvalidMergeFieldsFromText(variableData, text);
                        for (const variableDataErrorRecord of variableDataErrors) {
                            if (!variableDataErrorRecord.reportErrorRealtime) continue;

                            // look for each occurrence of the error field
                            while (text.indexOf(variableDataErrorRecord.tag) >= 0) {

                                const mergeIndex = text.indexOf(variableDataErrorRecord.tag);

                                // adjust the position related to the raw value to include the length of the merge field if the error merge field is currently stylized
                                if (mergeIndex >= 0 && mergeIndex <= range.index) {

                                    if (editor.getText().indexOf(variableDataErrorRecord.tag) >= 0) {
                                        editorStartPosition -= variableDataErrorRecord.tag.length - 1;
                                    }
                                    else {
                                        valueStartPosition += variableDataErrorRecord.tag.length - 1;
                                        if (getCursorPositionFromTag(originalText, variableDataErrorRecord.tag, valueStartPosition) === "right") {
                                            editorStartPosition += variableDataErrorRecord.tag.length - 1;
                                        }
                                    }

                                }

                                // remove the first occurrence of the error
                                text = text.replace(variableDataErrorRecord.tag, " ");

                            }
                        }
                    }

                    // find the current delta to later use to compare if any changes after adjusting selection position
                    const originalDelta = convertPlaintextToDelta(
                        convertHTMLContentToPlaintext(quill, editor.getHTML()),
                        variableData,
                        variableDataErrors,
                        selectionPosition.get(),
                        highlightInvalidMergeFields
                    );

                    // store the selection position information
                    selectionPosition.set({
                        editorStartPosition,
                        editorSelectedLength: range.length,
                        valueStartPosition,
                        valueSelectedLength,
                        valueLength,
                        mergeTagPositions: selectionPosition.get().mergeTagPositions ?? [],
                        hasFocus: true,
                    });

                    // find the new delta after changing selection position - position alone can change what is rendered
                    // with highlighting, so this will be used to decide if need to re-render the editor
                    const newDelta = convertPlaintextToDelta(
                        convertHTMLContentToPlaintext(quill, editor.getHTML()),
                        variableData,
                        variableDataErrors,
                        selectionPosition.get(),
                        highlightInvalidMergeFields
                    );

                    // if the delta has changed, then update the editor to reflect the new delta
                    if (JSON.stringify(originalDelta) !== JSON.stringify(newDelta)) {
                        await quill.setContents(newDelta, "silent");
                        const newPosition = selectionPosition.get().editorStartPosition;
                        if (isDevXd()) console.log("QuillTextEditor onChangeSelection calling setSelection with position "+newPosition, {selectionPosition: selectionPosition.get(), originalDelta, newDelta});
                        quill.setSelection(newPosition, 0, "self");
                    }
                    // TODO: Find out why length of selected merge tags are sometimes counted as 1 character, & sometimes 0
                    if (isDevXd()) console.log("selectionPosition: ", selectionPosition.get());
                }}

                // handle change events
                onChange={async (content, delta, source, editor) => {

                    if (!textFieldRef?.getEditor) return;
                    if (source === "silent") return;
                    const quill = textFieldRef.getEditor();

                    // check for broken merge fields and apply formatting if any found
                    let variableDataErrors = [];
                    if (highlightInvalidMergeFields) {
                        variableDataErrors = getInvalidMergeFieldsFromText(
                            variableData,
                            convertHTMLContentToPlaintext(quill, editor.getHTML())
                        );
                    }

                    // find the delta based from plaintext
                    delta = convertPlaintextToDelta(
                        convertHTMLContentToPlaintext(quill, editor.getHTML()),
                        variableData,
                        variableDataErrors,
                        selectionPosition?.get(),
                        highlightInvalidMergeFields
                    );

                    // TODO: Remove this after all tests pass and can confirm that this is no longer needed.
                    //
                    // there was code that used the following to assign to delta if included the span, but not sure what
                    // this was for or if it's still needed - When using quill.clipboard, there were cases where it
                    // rendered invalid merge tags as " Tag unrecognized{{tag}}" It seems to convert TemplateMarker
                    // and loses the error attribute in the process.
                    //
                    // if (content.indexOf("<span class=\"merge-marker\"") >= 0) {
                    //    console.log("FOUND MERGEMARKER", quill.clipboard.convert(content));
                    // }

                    const originalMergeTagPositions = selectionPosition.get().mergeTagPositions ?? [];
                    const originalMergeFieldLengthsBeforeCursor = originalMergeTagPositions.filter(x => x[0] < selectionPosition.get().editorStartPosition).map(x => x[1]).reduce((sum, x) => sum + x, 0);
                    const newMergeFieldPositions = getValidMergeTagPositions(delta);
                    const newMergeFieldLengthsBeforeCursor = newMergeFieldPositions.filter(x => x[0] < selectionPosition.get().editorStartPosition).map(x => x[1]).reduce((sum, x) => sum + x, 0);

                    // determine adjustment amount
                    let mergeFieldCursorAdjustment = 0;
                    // after completing typing a merge field, it will be stylized and reduce the cursor position by the merge field length minus one
                    if (newMergeFieldLengthsBeforeCursor > originalMergeFieldLengthsBeforeCursor) mergeFieldCursorAdjustment = newMergeFieldLengthsBeforeCursor - originalMergeFieldLengthsBeforeCursor - 1;

                    // record the merge tag and total character count to the selection position
                    selectionPosition.set({
                        ...selectionPosition.get(),
                        mergeTagPositions: newMergeFieldPositions
                    });

                    // apply merge field formatting if any found
                    if (delta.ops.find(x => x.insert?.TemplateMarker)) {

                        // if current text contains double newlines, then there is a single newline in the UI that needs to be applied to the delta
                        if (editor.getText().endsWith("\n\n")) {
                            delta.ops[delta.ops.findLastIndex(y => !!y.insert && !y.insert.TemplateMarker)].insert += "\n";
                        }

                        // update contents with the new delta
                        quill.setContents(delta, "silent");

                    }

                    // skip processing if the field doesn't have focus - this prevents additional logic from
                    // running when Quill is unnecessarily re-rendered
                    if (!selectionPosition?.get()?.hasFocus) return;

                    // find the text that should be applied in CC
                    let ccText = convertHTMLContentToPlaintext(quill, content);

                    // if the only difference is in a trailing return, don't continue to attempt a sync with CC
                    // the sync operation will remove the trailing newline
                    if (ccText.replace(/(\n)+$/g, "") === value.replace(/(\n)+$/g, "")) return;

                    // apply masking if required
                    if (inputMask) {
                        ccText = formatText(inputMask, ccText);
                        if (ccText === value) {
                            quill.setText(ccText);
                            quill.setSelection(
                                selectionPosition.get().editorStartPosition
                                + selectionPosition.get().editorSelectedLength
                                + (ccText.length + 1 - selectionPosition.get().valueLength),
                                0,
                                "silent"
                            );
                            return;
                        }
                    }

                    // end execution if no changes to send to CC
                    if (ccText === value) return;

                    // apply the change to CC
                    await onChange(ccText); // if don't await, then cursor is erratic, most noticeably when there is a merge field in the content

                    // set the start position to the current position, but if touching an error merge field, then
                    // include the merge field length as well, since this will not be stylized
                    const position = selectionPosition.get();
                    let startPosition = position.editorStartPosition;
                    for (const variableDataErrorRecord of variableDataErrors) {
                        if (getCursorPositionFromTag(ccText, variableDataErrorRecord.tag, position.valueStartPosition)) {
                            startPosition += variableDataErrorRecord.tag.length - 1;
                            break;
                        }
                    }

                    // reset the position - this places the cursor at the position of the end of the current
                    // selection, plus as many characters that were typed or pasted in, triggering this onchange
                    let newPosition = startPosition - mergeFieldCursorAdjustment;

                    // adjust for case where text is selected and press new key - without this, the cursor is moved to
                    // the start of the field when it should follow the new character, which happens automatically if
                    // deleting then typing
                    if (ccText.length === 1 && newPosition === 0 && position.editorSelectedLength > ccText.length)
                        newPosition = 1;
                    // set cursor at start of field if backspacing through the first character - without this, the
                    // cursor is moved after the first character
                    else if (newPosition === 1 && position.valueLength - ccText.length === 1)
                        newPosition = 0;

                    if (isDevXd()) console.log("QuillTextEditor onChange calling setSelection with position "+newPosition, {startPosition, mergeFieldCursorAdjustment, ccTextLength: ccText.length, editorSelectedLength: position.editorSelectedLength, position});
                    quill.setSelection(newPosition, 0, "api");

                }}
            />
        </div>
    ))

    .run()

);


// set up template markers for merge field chips
class TemplateMarker extends Embed {
    static create(value) {
        let node = super.create(value);

        // set general options
        node.setAttribute("class", cx("merge-marker", {"error": !!value.errorReason}));
        node.setAttribute("data-marker", value.mergeField);
        node.setAttribute("data-index", value.index);

        // set options specific to error states
        if (value.errorReason) node.setAttribute("data-error-reason", value.errorReason);

        node.innerHTML = value.displayText;
        return node;

    }
    static value(node) {
        return {
            mergeField: node.getAttribute("data-marker"),
            displayText: node.innerHTML.trim(),
            index: +node.getAttribute("data-index")
        };
    }
}

// create module to paste without formatting - this is registered only if requested
class PlainTextPaste extends Clipboard {
    onPaste (event) {
        event.preventDefault();
        const range = this.quill.getSelection();
        const text = event.clipboardData.getData("text/plain");
        const delta = new Delta().retain(range.index).delete(range.length).insert(text);
        const index = text.length + range.index;
        this.quill.updateContents(delta, "api");
        if (isDevXd()) console.log("QuillTextEditor PlainTextPaste calling setSelection with position "+index, {textLength: text.length, range, delta});
        this.quill.setSelection(index, 0, "api");
    }
}

// create module to handle events not directly supported by quill
class CustomEventHandlers {
    constructor(quill: any) {

        // add click handler
        quill.root.addEventListener("click", async (event) => {

            // selection is automatically placed, but it's placed before the merge tag if clicking the left side and
            // placed after if clicking the right side
            const currentSelection = quill.getSelection();
            if (!currentSelection) return;

            const clickedRemove = event.target.classList.contains("remove");
            const clickedMergeTagText = event.target.classList.contains("merge-tag-text");

            // set cursor position after the merge tag, then delete if requested
            if (clickedMergeTagText || clickedRemove) {

                let index = currentSelection.index;
                if (isDevXd()) console.log("QuillTextEditor CustomEventHandlers click calling setSelection with position "+index, currentSelection);
                await quill.setSelection(index, 0, "api");

                // delete the merge field if the remove button is clicked
                if (clickedRemove) {

                    // TODO: Fix bug where if {{city}}{{state}} and pressing to the left of close on {{state}}, then
                    // {{city}} is removed. See note in getDeltaIndexFromCursorPosition where index adjustment is made.
                    // This is a temporary workaround until we capture the click position relative the event target and
                    // can anticipate whether the cursor will be placed to the left or right of the merge field.

                    let delta = quill.getContents();
                    delete delta.ops[getDeltaIndexFromCursorPosition(delta, index)];
                    quill.setContents(delta, "api");

                }

            }

        });
    }
}

// TODO: find out if this can be used it may be a lot cleaner i was getting really strange behavior down this route
// but if we can get it to work, it might do a better job of handling cursor placement after pasting
// class copyMergeTags extends Clipboard {
//     constructor(quill, options) {
//         super(quill, options);
//         this.quill = quill;
//         this.quill.root.addEventListener('copy', this.onCopy.bind(this));
//         // this.quill.root.addEventListener('cut', this.onCopy.bind(this));
//     }

//     onCopy (event) {
//         // event.preventDefault();
//         const range = this.quill.getSelection();
//         const text = event.clipboardData.getData("text/plain");
//         const quill = this.quill

//         return this.quill.root.removeEventListener('copy', this.onCopy.bind(this));
//     }
// }


// convert quill content strings with HTML markup to plaintext strings to save to the order
const convertHTMLContentToPlaintext = (quill, value) => {

    let delta = quill.clipboard.convert(value);
    return delta.ops.map(x => x.insert.TemplateMarker?.mergeField ?? x.insert).join("");

}

// set up keyboard overrides - directly including within the <ReactQuill/> tag causes the editor to render empty, as
// does including in a chain service assignment, and providing the object through a function - setting as a constant
// object and assigning to the binding attribute is the only thing that seems to work correctly
const keyboardBindings = {
    tab: {
        key: Keyboard.keys.TAB,
        handler: (event) => {
            const currentEditor:any = document.activeElement;
            const focusableElements:any = document.querySelectorAll('a[href], button, input:not([style*="display:none"]):not([style*="display: none"]), textarea:not([style*="display:none"]):not([style*="display: none"]), select, details, [tabindex]:not([tabindex="-1"])');
            const idxCurrentEditor = Array.from(focusableElements).indexOf(currentEditor);
            currentEditor.blur();
            if (idxCurrentEditor + 1 === focusableElements.length) {
                focusableElements[0].focus();
            } else {
                focusableElements[idxCurrentEditor + 1].focus();
            }
        }
    }
};

// convert plaintext strings to quill delta format with chips in place of variable data tags
const convertPlaintextToDelta = (value, variableData, variableDataErrors = [], selectionPosition = null, highlightInvalidMergeFields = false) => {

    const plaintextValue = value;
    value = [value];

    let mergeFieldIndex = 1;

    // add markers for all variable data tags
    for (const variableRecord of variableData) {
        for (let i = value.length-1; i >= 0; i--) {
            if (typeof value[i] === "object") continue;
            let pieces = value[i].split(variableRecord.mergeField).flatMap(x => [x, {
                insert: {
                    TemplateMarker: {
                        mergeField: variableRecord.mergeField,
                        // DO NOT nest html tags below - this causes issues with cursor placement logic and it breaks
                        // in non-obvious ways
                        // ✖ character may work if class "fa fa-times" doesn't work
                        displayText: '<span class="remove">✖</span>'
                            + '<span class="merge-tag-text" data-reference="'
                            + variableRecord.mergeField
                            + '">'
                            + variableRecord.name
                            + '</span>',
                        index: mergeFieldIndex++
                    }
                }
            }]).slice(0, -1);
            value.splice(i, 1, ...pieces);
        }
    }

    // add markers for all variable data tags with errors
    if (highlightInvalidMergeFields) {
        for (const variableErrorRecord of variableDataErrors) {
            for (let i = value.length-1; i >= 0; i--) {
                if (typeof value[i] === "object") continue;
                if (!variableErrorRecord.reportErrorRealtime) continue;

                // do not format the error tag if the cursor is adjacent or within it
                if (getCursorPositionFromTag(plaintextValue, variableErrorRecord.tag, selectionPosition.valueStartPosition)) continue;

                // convert the tag to a marker with error styling
                let pieces = value[i].split(variableErrorRecord.tag).flatMap(x => [x, {
                    insert: {
                        TemplateMarker: {
                            mergeField: variableErrorRecord.tag,
                            displayText: '<span class="tooltip">'+variableErrorRecord.reason+'</span>'+variableErrorRecord.tag,
                            index: mergeFieldIndex++,
                            errorReason: variableErrorRecord.reason
                        }
                    }
                }]).slice(0, -1);
                value.splice(i, 1, ...pieces);
            }
        }
    }

    return {
        ops: value.map(x => (typeof x === "object") ? x : {insert: x})
    };

}

// checks if the current cursor position is next to the provided tag - if related, will return which position the cursor
// is in relation to the tag
const getCursorPositionFromTag = (text, tag, selectionPosition):Types.Common.UiComponents.QuillTextEditor.CursorToTagRelationship => {

    let currentTagPositionOffset = 0;
    let textPieces = text.split(tag);
    textPieces.pop();

    for (const tagPieceLength of textPieces.map(x => x.length)) {
        currentTagPositionOffset += tagPieceLength;

        if (selectionPosition === currentTagPositionOffset) {
            return "left";
        }
        else if (selectionPosition === currentTagPositionOffset + tag.length) {
            return "right";
        }
        else if (selectionPosition > currentTagPositionOffset
            && selectionPosition < currentTagPositionOffset + tag.length) {
            return "inside";
        }

        currentTagPositionOffset += tag.length;
    }

    return "";

};

const getValidMergeTagPositions = (delta) => {

    let positions = [];

    let currentPosition = 0;
    for(const op of delta.ops) {

        if (op.insert?.TemplateMarker) {
            if (!op.insert?.TemplateMarker.errorReason) {
                positions.push([currentPosition, op.insert.TemplateMarker.mergeField.length]);
                currentPosition += 1; // merge field counts for only one character
            }
            else currentPosition += op.insert.TemplateMarker.mergeField.length; // error fields count for the length of the merge field
        }
        else currentPosition += op.insert.length;

    }

    return positions;

};

const getDeltaIndexFromCursorPosition = (delta, cursorPosition) => {

    let index = 0;

    let currentPosition = 0;
    for (let i = 0; i < delta.ops.length; i++) {

        if (delta.ops[i].insert?.TemplateMarker) {
            if (!delta.ops[i].insert?.TemplateMarker.errorReason) currentPosition += 1;
            else currentPosition += delta.ops[i].insert.TemplateMarker.mergeField.length;
        }
        else currentPosition += delta.ops[i].insert.length;

        if (cursorPosition <= currentPosition) break;
        index++;
    }

    // quill adjusts the cursor position to be to the left or right of the merge tag depending on the position of the
    // pointer during the click - if placed before the merge tag, then the index that was determined above will not
    // represent a merge tag - adjust to the next index if this is the case
    if (!delta.ops[index]?.insert?.TemplateMarker || delta.ops[index]?.insert?.TemplateMarker.errorReason) {
        index++;
    }

    return index;

};

export {
    QuillTextEditor
};
